/**
 * Line-based unified diff (Doc 21 Fase 7).
 *
 * Implementación local, sin dependencias externas. Para inputs típicos
 * del CommentAiAssistModal (rewrites de secciones rich_text de ~500-2000
 * chars), el costo O(m*n) es trivial. Si en el futuro el modal abre
 * comparaciones de documentos completos, conviene migrar a `diff`
 * (jsdiff) que usa Myers con heurísticas para casos grandes.
 *
 * Algoritmo: LCS clásico via DP. Reconstruye la lista de líneas
 * marcadas como `eq` (sin cambio), `add` (nueva), `del` (eliminada). El
 * orden preserva ambos lados — las `del` aparecen antes que las `add`
 * en la misma posición divergente, igual que un unified diff.
 */

export type DiffLineType = 'eq' | 'add' | 'del'

export interface DiffLine {
  type: DiffLineType
  text: string
}

export function diffLines(a: string, b: string): DiffLine[] {
  const aLines = a.split('\n')
  const bLines = b.split('\n')
  const m = aLines.length
  const n = bLines.length

  // dp[i][j] = LCS length de aLines[0..i] y bLines[0..j].
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    Array.from({ length: n + 1 }, () => 0),
  )
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (aLines[i - 1] === bLines[j - 1]) {
        dp[i]![j] = dp[i - 1]![j - 1]! + 1
      } else {
        dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!)
      }
    }
  }

  // Backtrack desde (m, n). Preferimos `del` antes que `add` cuando los
  // dp scores son iguales — es la convención clásica del unified diff.
  const out: DiffLine[] = []
  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && aLines[i - 1] === bLines[j - 1]) {
      out.unshift({ type: 'eq', text: aLines[i - 1]! })
      i--
      j--
    } else if (i > 0 && (j === 0 || dp[i - 1]![j]! >= dp[i]![j - 1]!)) {
      out.unshift({ type: 'del', text: aLines[i - 1]! })
      i--
    } else {
      out.unshift({ type: 'add', text: bLines[j - 1]! })
      j--
    }
  }

  return out
}

/**
 * Resumen de cambios para mostrar "+12 / −7" debajo del diff. Las
 * líneas vacías se cuentan (afectan el visual del rewrite). */
export interface DiffStats {
  added: number
  removed: number
  unchanged: number
}

export function summarizeDiff(diff: DiffLine[]): DiffStats {
  const stats: DiffStats = { added: 0, removed: 0, unchanged: 0 }
  for (const line of diff) {
    if (line.type === 'add') stats.added++
    else if (line.type === 'del') stats.removed++
    else stats.unchanged++
  }
  return stats
}
